<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Page Header integrated into Form Card -->
            <v-card-title class="page-header py-2 px-3">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">Add New Tag</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Create a new tag for organizing and categorizing content
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Back Button and Form Badge -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip color="info" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-tag-plus</v-icon>
                      New Tag Form
                    </v-chip>
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Tags
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <v-form ref="form" v-model="valid" lazy-validation>
                <!-- Tag Details Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-tag</v-icon>
                    Tag Details
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.name"
                        label="Tag Name"
                        placeholder="Enter tag name"
                        outlined
                        :rules="[(v) => !!v || 'Tag name is required']"
                        :error-messages="errors.name"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-tag</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12">
                      <v-textarea
                        v-model="form.description"
                        label="Tag Description (Optional)"
                        placeholder="Enter detailed tag description"
                        outlined
                        rows="4"
                        :error-messages="errors.description"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-text</v-icon>
                        </template>
                      </v-textarea>
                    </v-col>
                  </v-row>
                </div>

                <!-- Tag Settings Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-cog</v-icon>
                    Tag Settings
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-card outlined class="status-card">
                        <v-card-text class="pa-2">
                          <div class="d-flex align-center">
                            <v-icon color="grey" class="mr-3"
                              >mdi-tag-check</v-icon
                            >
                            <div class="flex-grow-1">
                              <div class="font-weight-medium mb-1">
                                Tag Status
                              </div>
                              <div class="text-caption grey--text">
                                Set the initial status for this tag
                              </div>
                            </div>
                            <v-switch
                              v-model="form.is_active"
                              color="success"
                              inset
                              hide-details
                              class="ml-3"
                            ></v-switch>
                          </div>
                          <div class="mt-2">
                            <v-chip
                              small
                              :color="form.is_active ? 'success' : 'warning'"
                              :text-color="form.is_active ? 'white' : 'black'"
                            >
                              {{ form.is_active ? "Active" : "Inactive" }}
                            </v-chip>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
              </v-form>
            </v-card-text>

            <!-- Form Actions Footer -->
            <v-divider></v-divider>
            <v-card-actions class="form-actions pa-2">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <v-chip
                      small
                      :color="hasValidationErrors() ? 'error' : 'success'"
                      :text-color="hasValidationErrors() ? 'white' : 'white'"
                      class="mr-3"
                    >
                      <v-icon left x-small>
                        {{
                          hasValidationErrors()
                            ? "mdi-alert-circle"
                            : "mdi-check-circle"
                        }}
                      </v-icon>
                      {{
                        hasValidationErrors()
                          ? "Please fix validation errors"
                          : "Form validation passed"
                      }}
                    </v-chip>
                    <span class="text-caption grey--text">
                      All required fields must be completed
                    </span>
                  </div>
                </v-col>

                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex justify-end">
                    <v-btn
                      color="grey"
                      outlined
                      class="mr-3 action-button"
                      @click="$router.back()"
                      :disabled="isSaving"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      color="primary"
                      class="action-button"
                      @click="saveTag"
                      :loading="isSaving"
                      :disabled="isSaving || !valid"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      Save Tag
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Success Dialog -->
      <v-dialog v-model="showSuccessDialog" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-check-circle
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Tag created successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="
                          showSuccessDialog = false;
                          $router.push('/tags');
                        "
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "TagCreatePage",
  data() {
    return {
      valid: false,
      form: {
        name: "",
        description: "",
        is_active: true,
      },
      errors: {},
      isSaving: false,
      showSuccessDialog: false,
    };
  },
  methods: {
    hasValidationErrors() {
      return Object.keys(this.errors).length > 0 || !this.valid;
    },

    async saveTag() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.isSaving = true;
      this.errors = {};

      try {
        // Prepare the data
        const tagData = {
          name: this.form.name,
          description: this.form.description,
          is_active: this.form.is_active,
        };

        const response = await axios.post("/tags", tagData);

        if (response.data.success) {
          this.showSuccessDialog = true;
          this.resetForm();
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              response.data.message ||
              "An error occurred while creating the tag.",
          });
        }
      } catch (error) {
        console.error(error);

        if (error.response) {
          if (error.response.status === 422) {
            // Validation errors
            this.errors = error.response.data.errors || {};
          } else if (error.response.status === 401) {
            // Authentication error
            this.$store.dispatch("errors/setError", {
              message: "Authentication required. Please login again.",
            });
            this.$router.push("/login");
          } else {
            // Other errors
            this.$store.dispatch("errors/setError", {
              message:
                error.response.data.message ||
                "An error occurred while creating the tag.",
            });
          }
        } else {
          // Network or other errors
          this.$store.dispatch("errors/setError", {
            message:
              "Network error. Please check your connection and try again.",
          });
        }
      } finally {
        this.isSaving = false;
      }
    },

    resetForm() {
      this.form = {
        name: "",
        description: "",
        is_active: true,
      };
      this.errors = {};
      this.$refs.form.resetValidation();
    },

    validateForm() {
      const errors = {};

      if (!this.form.name || this.form.name.trim() === "") {
        errors.name = ["Tag name is required"];
      }

      if (this.form.name && this.form.name.length > 255) {
        errors.name = ["Tag name may not be greater than 255 characters"];
      }

      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
  },

  watch: {
    // Clear errors when user starts typing
    "form.name"() {
      if (this.errors.name) {
        this.$delete(this.errors, "name");
      }
    },
    "form.description"() {
      if (this.errors.description) {
        this.$delete(this.errors, "description");
      }
    },
  },
};
</script>

<style scoped>
/* Professional Form Card with Integrated Header */
.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  border-left: 4px solid var(--v-primary-base);
}

.page-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.back-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

.form-content {
  background: #ffffff;
}

/* Section Styling */
.form-section {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 0.5rem;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

/* Professional Form Fields */
.professional-field >>> .v-input__control {
  min-height: 40px;
}

.professional-field >>> .v-text-field__details {
  padding-top: 4px;
}

.professional-field >>> .v-input__slot {
  border-radius: 4px;
}

.professional-field >>> .v-text-field input {
  font-size: 0.75rem;
  padding-top: 8px;
  padding-bottom: 8px;
}

.professional-field >>> .v-label {
  font-size: 0.75rem;
  font-weight: 500;
}

.professional-field >>> .v-icon {
  color: #6c757d !important;
}

.professional-field >>> .v-textarea textarea {
  font-size: 0.75rem;
  line-height: 1.4;
}

/* Status Card */
.status-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.status-card:hover {
  border-color: var(--v-primary-base);
  background-color: #f8f9fa;
}

/* Form Actions */
.form-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid #dee2e6;
}

.action-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  border-radius: 4px;
  font-size: 0.75rem;
}

/* Spacing and Alignment */
.v-row {
  margin-left: -6px;
  margin-right: -6px;
}

.v-row > .v-col {
  padding: 0 6px;
}

/* Professional Color Scheme */
.v-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
}

.v-btn.v-btn--outlined {
  border: 2px solid currentColor;
}

.v-chip {
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 960px) {
  .page-title {
    font-size: 1.1rem;
  }

  .form-title {
    font-size: 0.9rem;
  }

  .section-title {
    font-size: 0.85rem;
  }

  .form-content {
    padding: 0.75rem !important;
  }

  .back-button,
  .action-button {
    min-width: 80px;
    font-size: 0.7rem;
  }
}

@media (max-width: 600px) {
  .v-row > .v-col {
    padding: 0 4px;
  }

  .form-content {
    padding: 0.5rem !important;
  }

  .section-title {
    font-size: 0.8rem;
  }

  .professional-field >>> .v-input__control {
    min-height: 36px;
  }

  .professional-field >>> .v-text-field input {
    font-size: 0.7rem;
    padding-top: 6px;
    padding-bottom: 6px;
  }

  .back-button,
  .action-button {
    min-width: 70px;
    height: 28px;
    font-size: 0.65rem;
  }
}

/* Clean Focus States */
.professional-field >>> .v-input--is-focused .v-input__slot {
  border-color: var(--v-primary-base) !important;
  border-width: 2px !important;
}

/* Consistent Margins */
.mb-1 {
  margin-bottom: 0.5rem !important;
}

.mb-2 {
  margin-bottom: 0.75rem !important;
}

.mb-3 {
  margin-bottom: 1rem !important;
}

/* Professional Transitions */
.v-card,
.v-btn,
.professional-field,
.status-card {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>